import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { CreditGateState } from './useCreditGate'

/**
 * Port of the legacy "Payment overdue" banner shown at the top of
 * `MomentumEditComponent`/`IndicatorconditionEditComponent`/
 * `CoinpairlistEditComponent` when `canEditItems == 0` (negative credit
 * balance). `itemLabel` reproduces the per-page wording difference legacy
 * had ("manage your momentums" vs "manage your settings").
 */
export function CreditOverdueBanner({ gate, itemLabel }: { gate: CreditGateState; itemLabel: string }) {
  if (gate.canEdit !== 0) return null
  return (
    <div className="screener-alert screener-alert-danger">
      <strong>Payment overdue</strong>
      <br />
      You have a negative credit balance, to manage your {itemLabel}, please first resolve the{' '}
      <Link to="/payment">payment issues</Link>.
    </div>
  )
}

/**
 * Port of the legacy Save-button gating block: disabled while
 * `canEditItems < 1` (loading or overdue); once allowed, either the normal
 * submit button or — if a payment method still needs to be set up — a
 * disabled button plus a link to `/payment`. Matches legacy's exact
 * (slightly odd) edge case: while `paymentNeeded` is true and the payment
 * method check hasn't resolved yet, legacy rendered NEITHER branch, so no
 * button at all appears momentarily — reproduced here rather than "fixed",
 * per the round-1 straight-conversion scope.
 */
export function GatedSaveButton({
  gate,
  saving,
  label = 'Save',
  savingLabel = 'Save …',
}: {
  gate: CreditGateState
  saving: boolean
  label?: ReactNode
  savingLabel?: ReactNode
}) {
  if (gate.canEdit < 1) {
    return (
      <button type="button" disabled>
        {label}
      </button>
    )
  }

  if (gate.paymentNeeded) {
    if (!gate.paymentMethodLoaded) return null
    return (
      <>
        <button type="button" disabled>
          {label}
        </button>
        <p className="screener-text-info">
          To modify this item, please <Link to="/payment">set up your payment method</Link> first!
        </p>
      </>
    )
  }

  return (
    <button type="submit" disabled={saving}>
      {saving ? savingLabel : label}
    </button>
  )
}
